/**
 * Attribution Tracker — Bloque 2.
 *
 * Reads UTMs + fbclid + referrer at every page load, persists to sessionStorage
 * under the key `fcc_attribution`. NEVER overwrites once set so the original
 * source is preserved across the entire visit.
 *
 * Public API:
 *   initAttributionTracker()         — call once at app boot (App.js)
 *   getAttribution()                 — read the stored object
 *   adoptCampaignUtms(utms)          — fill UTMs from an internal link when the session has no campaign
 *   submitAttribution({ user_id, temp_email, conversion_event, product_interest })
 *                                    — send to backend at every conversion event
 *   clearAttribution()               — reset (useful after successful purchase to attribute fresh visits)
 */
import api from './api';

const STORAGE_KEY = 'fcc_attribution';
const SESSION_ID_KEY = 'fcc_session_id';

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid'];

const SAME_DOMAIN_HINTS = ['feconciencia.com', 'feconciencia-fix', 'preview.emergentagent', 'localhost'];

function generateSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

function ensureSessionId() {
  if (typeof window === 'undefined') return null;
  let sid = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sid) {
    sid = generateSessionId();
    sessionStorage.setItem(SESSION_ID_KEY, sid);
  }
  return sid;
}

function readUrlParams() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const out = {};
  for (const key of UTM_PARAMS) {
    const v = params.get(key);
    if (v && v.trim()) out[key] = v.trim();
  }
  return out;
}

function isExternalReferrer(referrer) {
  if (!referrer) return false;
  try {
    const refUrl = new URL(referrer);
    const refHost = refUrl.hostname.toLowerCase();
    return !SAME_DOMAIN_HINTS.some((hint) => refHost.includes(hint));
  } catch {
    return false;
  }
}

function domainOf(url) {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * Initialize the tracker. Call at app boot. Safe to call repeatedly — it short-circuits
 * if attribution is already locked in this session.
 */
export function initAttributionTracker() {
  if (typeof window === 'undefined') return null;

  ensureSessionId();

  // If we already locked attribution this session, keep it untouched.
  const existingRaw = sessionStorage.getItem(STORAGE_KEY);
  if (existingRaw) {
    try {
      return JSON.parse(existingRaw);
    } catch {
      // corrupted; fall through and rewrite
    }
  }

  const urlParams = readUrlParams();
  const referrer = document.referrer || '';
  const landing = window.location.href;

  let attribution;
  if (Object.keys(urlParams).length > 0) {
    // Case 1 — UTMs present in URL
    attribution = {
      utm_source: urlParams.utm_source || null,
      utm_medium: urlParams.utm_medium || null,
      utm_campaign: urlParams.utm_campaign || null,
      utm_content: urlParams.utm_content || null,
      utm_term: urlParams.utm_term || null,
      fbclid: urlParams.fbclid || null,
      landing_page: landing,
      referrer,
    };
  } else if (isExternalReferrer(referrer)) {
    // Case 2 — no UTMs but external referrer
    attribution = {
      utm_source: domainOf(referrer) || 'referral',
      utm_medium: 'referral',
      utm_campaign: null,
      utm_content: null,
      utm_term: null,
      fbclid: null,
      landing_page: landing,
      referrer,
    };
  } else {
    // Case 3 — direct
    attribution = {
      utm_source: 'direct',
      utm_medium: 'none',
      utm_campaign: null,
      utm_content: null,
      utm_term: null,
      fbclid: null,
      landing_page: landing,
      referrer: referrer || null,
    };
  }

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  return attribution;
}

export function getAttribution() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Adopt UTMs from an internal link / client-side redirect (e.g. /links → /curso/ayeka,
 * /ayeka → /curso/ayeka?utm_…) only when the session has no campaign yet. A real
 * paid/email campaign (utm_campaign already set) is never overwritten. If the session
 * was organic (direct / referral) the given UTMs replace the fallback source/medium;
 * otherwise only the missing keys are filled. fbclid, referrer and landing_page are kept.
 */
export function adoptCampaignUtms(utms = {}) {
  if (typeof window === 'undefined') return null;
  const current = getAttribution() || initAttributionTracker();
  if (!current || current.utm_campaign) return current;
  const incoming = UTM_PARAMS.filter((k) => k !== 'fbclid' && utms[k]);
  if (incoming.length === 0) return current;
  const organic = current.utm_medium === 'none' || current.utm_medium === 'referral';
  const next = { ...current };
  incoming.forEach((k) => {
    if (organic || !current[k]) next[k] = utms[k];
  });
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* noop */ }
  return next;
}

export function getSessionId() {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(SESSION_ID_KEY);
}

export function clearAttribution() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(SESSION_ID_KEY);
}

/**
 * Submit attribution to backend at a conversion point.
 * Non-blocking: failures are logged but never throw.
 *
 * @param {Object} opts
 * @param {string} [opts.user_id]           — if user is logged in / just created
 * @param {string} [opts.temp_email]        — when no user_id yet (RC apply without account)
 * @param {string} opts.conversion_event    — 'trial_started' | 'application_submitted' | 'purchase' | 'visit'
 * @param {string} [opts.product_interest]  — 'ayeka' | 'ruta_critica' (overrides inference)
 */
export async function submitAttribution(opts) {
  const attribution = getAttribution() || initAttributionTracker() || {};
  const sessionId = getSessionId();

  const payload = {
    session_id: sessionId,
    utm_source: attribution.utm_source || 'direct',
    utm_medium: attribution.utm_medium || 'none',
    utm_campaign: attribution.utm_campaign || null,
    utm_content: attribution.utm_content || null,
    utm_term: attribution.utm_term || null,
    fbclid: attribution.fbclid || null,
    landing_page: attribution.landing_page || null,
    referrer: attribution.referrer || null,
    user_id: opts?.user_id || null,
    temp_email: opts?.temp_email || null,
    product_interest: opts?.product_interest || null,
    conversion_event: opts?.conversion_event || 'visit',
  };

  try {
    const r = await api.post('/attribution/capture', payload);
    return r.data;
  } catch (e) {
    console.warn('[attribution] capture failed (non-blocking):', e?.message);
    return null;
  }
}
